import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import { trackEvent } from '../../../services/analyticsService';
import type { SideFoodChip, SideSuggester, SidesWithFoods } from './useSideSuggestions';

/** What the sheet and the drawer draw above the accompaniment list (#853). */
export interface SideFoodsProps {
  foods: SideFoodChip[];
  picked: string[];
  onToggle: (id: string) => void;
  note?: string;
}

/**
 * The foods a cook picks in the accompaniment list, for one opening of it.
 * `openingKey` changes every time the list opens (the hosts' fan seed), and the
 * picks belong to that opening only: the next dish starts with none.
 */
export function useSideFoodPicks({ suggester, slot, active, openingKey, surface }: {
  suggester: Pick<SideSuggester, 'foodsForSlot' | 'withFoodsForSlot'>;
  slot: PlannedMeal[] | undefined;
  /** The accompaniment list is open. */
  active: boolean;
  openingKey: number;
  surface: 'mobile_sheet' | 'desktop_drawer';
}): { sideFoods?: SideFoodsProps; withFoods: SidesWithFoods | null } {
  const { t } = useTranslation();
  const { foodsForSlot, withFoodsForSlot } = suggester;
  const [state, setState] = useState<{ key: number; picked: string[] }>({ key: openingKey, picked: [] });
  const picked = useMemo(
    () => (active && state.key === openingKey ? state.picked : []),
    [active, state, openingKey],
  );

  const foods = useMemo(() => (active ? foodsForSlot(slot) : []), [active, foodsForSlot, slot]);
  const withFoods = useMemo(
    () => (picked.length > 0 ? withFoodsForSlot(slot, picked) : null),
    [picked, withFoodsForSlot, slot],
  );

  const onToggle = useCallback((id: string) => {
    const next = picked.includes(id) ? picked.filter((f) => f !== id) : [...picked, id];
    setState({ key: openingKey, picked: next });
    trackEvent('side_food_toggled', { food: id, on: next.includes(id), picked_count: next.length, surface });
  }, [picked, openingKey, surface]);

  const note = withFoods && !withFoods.together
    ? t('sideFoods.noneTogether', { foods: withFoods.foodsPhrase })
    : undefined;

  return {
    sideFoods: active && foods.length > 0 ? { foods, picked, onToggle, note } : undefined,
    withFoods,
  };
}
